import { useState, useEffect, useCallback, useRef } from 'react';
import { Search, Filter, FileText, XCircle, Loader2, ChevronLeft, ChevronRight, CheckCircle, CreditCard, Banknote, ArrowRight, User, X, Printer, RotateCcw } from 'lucide-react';
import { useReactToPrint } from 'react-to-print';
import { useAuthStore } from '@/stores/auth-store';
import { ventasService } from '@/services/ventas.service';
import { useApiError } from '@/hooks/useApiError';
import { ApiErrorDisplay } from '@/components/ApiErrorDisplay';
import { AdminAuthModal } from '@/components/AdminAuthModal';
import { dialogs } from '@/stores/dialog-store';
import { cajaService } from '@/services/caja.service';
import { SaleTicket } from '@/components/SaleTicket';
import { ReturnModal } from './ReturnModal';

const statusColors: Record<string, string> = {
  COMPLETED: 'bg-ios-green/10 text-ios-green',
  CANCELLED: 'bg-red-50 text-ios-red',
  REFUNDED: 'bg-yellow-50 text-yellow-600',
  PENDING_PAYMENT: 'bg-blue-50 text-ios-blue',
};

const statusLabels: Record<string, string> = {
  COMPLETED: 'Completada',
  CANCELLED: 'Cancelada',
  REFUNDED: 'Reembolsada',
  PENDING_PAYMENT: 'Nota / Pendiente',
};

const paymentLabels: Record<string, string> = {
  CASH: 'Efectivo',
  CARD: 'Tarjeta',
  TRANSFER: 'Transferencia',
  CREDIT: 'Crédito',
  MIXED: 'Mixto',
};

interface SalesHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function SalesHistoryModal({ isOpen, onClose }: SalesHistoryModalProps) {
  const { user } = useAuthStore();
  const [sales, setSales] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const today = new Date().toLocaleDateString('en-CA');
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [selectedSale, setSelectedSale] = useState<any>(null);
  const [showDetail, setShowDetail] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<'CASH' | 'CARD' | 'TRANSFER' | 'CREDIT'>('CASH');
  const [cashReceived, setCashReceived] = useState('');
  const [cardReference, setCardReference] = useState('');
  const [cardLast4, setCardLast4] = useState('');
  const [paying, setPaying] = useState(false);
  const [cashRegister, setCashRegister] = useState<any>(null);
  
  const [showAdminAuth, setShowAdminAuth] = useState(false);
  const [pendingCancelId, setPendingCancelId] = useState<string | null>(null);
  const [showReturnModal, setShowReturnModal] = useState(false);

  const printRef = useRef<HTMLDivElement>(null);
  const [saleToPrint, setSaleToPrint] = useState<any>(null);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    onAfterPrint: () => setSaleToPrint(null),
  });

  useEffect(() => {
    if (saleToPrint) {
      setTimeout(() => {
        handlePrint();
      }, 100);
    }
  }, [saleToPrint, handlePrint]);

  const { error, errorType, handleApiError, handleAuthRedirect } = useApiError();
  const limit = 20;

  const isAdmin = ['SUPER_ADMIN', 'ADMIN', 'GERENTE'].includes(user?.role || '');

  useEffect(() => {
    if (user?.branch?.id) {
      cajaService.getStatus(user.branch.id).then(setCashRegister).catch(() => {});
    }
  }, [user]);

  const loadSales = useCallback(async () => {
    if (!isOpen || !user?.company?.id) return;
    setLoading(true);
    try {
      const result = await ventasService.findAll({
        companyId: user.company.id,
        branchId: user.branch?.id,
        search: search || undefined,
        status: statusFilter || undefined,
        startDate: startDate ? new Date(`${startDate}T00:00:00`).toISOString() : undefined,
        endDate: endDate ? new Date(`${endDate}T23:59:59`).toISOString() : undefined,
        page,
        limit,
      });
      setSales(result.data || []);
      setTotal(result.total || 0);
      setTotalPages(result.totalPages || 1);
    } catch (err: any) { handleApiError(err); }
    setLoading(false);
  }, [user, search, statusFilter, startDate, endDate, page]);

  useEffect(() => { loadSales(); }, [loadSales]);

  const handleCancel = async (saleId: string) => {
    if (!user?.id) return;
    if (!isAdmin) {
      setPendingCancelId(saleId);
      setShowAdminAuth(true);
      return;
    }
    
    if (!await dialogs.confirm('¿Estás seguro de cancelar esta venta? Se restaurará el inventario.')) return;
    executeCancel(saleId);
  };

  const executeCancel = async (saleId: string, adminToken?: string) => {
    setCancelling(true);
    try {
      await ventasService.cancel(saleId, { cancelledById: user!.id, reason: 'Cancelación manual' }, adminToken);
      loadSales();
      if (selectedSale?.id === saleId) {
        setSelectedSale((prev: any) => prev ? { ...prev, status: 'CANCELLED' } : null);
      }
      setShowAdminAuth(false);
      setPendingCancelId(null);
      if (adminToken) {
        dialogs.alert('Venta cancelada exitosamente con autorización.');
      }
    } catch (err: any) { handleApiError(err); }
    setCancelling(false);
  };

  const handleAdminAuthSuccess = async (adminToken: string) => {
    if (pendingCancelId) {
      if (!await dialogs.confirm('Administrador verificado. ¿Confirmas la cancelación de esta venta? Se restaurará el inventario.')) {
        setShowAdminAuth(false);
        setPendingCancelId(null);
        return;
      }
      executeCancel(pendingCancelId, adminToken);
    }
  };

  const viewDetail = async (id: string) => {
    try {
      const sale = await ventasService.findById(id);
      setSelectedSale(sale);
      setShowPayment(false);
      setShowDetail(true);
    } catch (err: any) { handleApiError(err); }
  };

  const handlePay = async () => {
    if (!selectedSale || !user?.company?.id) return;
    setPaying(true);
    try {
      const grandTotal = Number(selectedSale.grandTotal);
      const payments = [{ method: paymentMethod, amount: grandTotal, reference: cardReference || undefined, cardLast4: cardLast4 || undefined }];
      
      await ventasService.pay(selectedSale.id, {
        cashRegisterId: cashRegister?.id,
        payments,
        paidAmount: grandTotal,
        changeAmount: paymentMethod === 'CASH' ? Math.max(0, parseFloat(cashReceived || '0') - grandTotal) : 0,
      });
      setShowPayment(false);
      setShowDetail(false);
      setCashReceived('');
      setCardReference('');
      setCardLast4('');
      loadSales();
      dialogs.alert('Nota cobrada exitosamente');
    } catch (err: any) { handleApiError(err); }
    setPaying(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex bg-black/50 p-4 sm:p-6 lg:p-8">
      <div className="bg-white rounded-3xl shadow-2xl w-full h-full flex flex-col overflow-hidden relative">
        <div className="absolute top-4 right-4 z-10">
          <button onClick={onClose} className="p-3 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors text-gray-500">
            <X size={24} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          <ApiErrorDisplay error={error} errorType={errorType} onRetry={loadSales} onLogin={handleAuthRedirect} />
          {!error && (
          <>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Historial de Ventas</h1>
          <p className="text-sm text-ios-gray mt-1">{total} ventas registradas</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ios-gray" />
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Buscar folio..."
            className="ios-input w-full pl-9 h-9 text-sm"
          />
        </div>
        {isAdmin && (
          <>
            <input 
              type="date"
              value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
              className="ios-input h-9 text-sm py-1"
            />
            <span className="text-ios-gray">-</span>
            <input 
              type="date"
              value={endDate}
              onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
              className="ios-input h-9 text-sm py-1"
            />
          </>
        )}
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="ios-input h-9 text-sm py-1"
        >
          <option value="">Todos los estados</option>
          <option value="PENDING_PAYMENT">Notas Pendientes</option>
          <option value="COMPLETED">Completadas</option>
          <option value="CANCELLED">Canceladas</option>
        </select>
      </div>

      {/* Sales table */}
      <div className="ios-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-ios-gray-5 bg-ios-gray-6/50">
                <th className="text-left px-4 py-3 font-medium text-ios-gray text-xs uppercase tracking-wider">Folio</th>
                <th className="text-left px-4 py-3 font-medium text-ios-gray text-xs uppercase tracking-wider">Fecha</th>
                <th className="text-left px-4 py-3 font-medium text-ios-gray text-xs uppercase tracking-wider">Cliente</th>
                <th className="text-left px-4 py-3 font-medium text-ios-gray text-xs uppercase tracking-wider">Método</th>
                <th className="text-right px-4 py-3 font-medium text-ios-gray text-xs uppercase tracking-wider">Total</th>
                <th className="text-center px-4 py-3 font-medium text-ios-gray text-xs uppercase tracking-wider">Estado</th>
                <th className="text-right px-4 py-3 font-medium text-ios-gray text-xs uppercase tracking-wider">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ios-gray-5">
              {loading ? (
                <tr>
                  <td colSpan={7} className="text-center py-12">
                    <Loader2 size={24} className="animate-spin mx-auto text-ios-gray" />
                  </td>
                </tr>
              ) : sales.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-12 text-ios-gray text-sm">
                    No se encontraron ventas
                  </td>
                </tr>
              ) : (
                sales.map((sale) => (
                  <tr key={sale.id} className="hover:bg-ios-gray-6/50 transition-colors cursor-pointer" onClick={() => viewDetail(sale.id)}>
                    <td className="px-4 py-3 font-medium">{sale.folio}</td>
                    <td className="px-4 py-3 text-ios-gray text-xs">
                      {new Date(sale.createdAt).toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </td>
                    <td className="px-4 py-3">{sale.customer?.name || '—'}</td>
                    <td className="px-4 py-3 text-xs">{paymentLabels[sale.paymentMethod] || sale.paymentMethod}</td>
                    <td className="px-4 py-3 text-right font-semibold">${Number(sale.grandTotal).toFixed(2)}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${statusColors[sale.status] || ''}`}>
                        {statusLabels[sale.status] || sale.status}
                      </span>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-1 justify-end">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSaleToPrint(sale);
                          }}
                          className="p-1.5 text-blue-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Reimprimir Ticket"
                        >
                          <Printer size={16} />
                        </button>
                        {sale.status === 'COMPLETED' && (
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowReturnModal(true);
                            }}
                            className="p-1.5 text-orange-500 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                            title="Generar Nota de Crédito por devolución"
                          >
                            <RotateCcw size={16} />
                          </button>
                        )}
                        {sale.status !== 'CANCELLED' && (
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCancel(sale.id);
                            }}
                            className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Cancelar Venta"
                          >
                            <XCircle size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-ios-gray-5">
            <span className="text-xs text-ios-gray">Página {page} de {totalPages}</span>
            <div className="flex gap-1">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-1.5 rounded-lg hover:bg-ios-gray-6 disabled:opacity-50"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-1.5 rounded-lg hover:bg-ios-gray-6 disabled:opacity-50"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Sale Detail Modal */}
      {showDetail && selectedSale && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-[20px] w-full max-w-lg max-h-[90vh] flex flex-col shadow-ios-xl">
            <div className="flex items-center justify-between p-4 border-b border-ios-gray-5">
              <div>
                <h3 className="font-semibold">Venta {selectedSale.folio}</h3>
                <p className="text-xs text-ios-gray">{new Date(selectedSale.createdAt).toLocaleString('es-MX')}</p>
              </div>
              <button onClick={() => setShowDetail(false)} className="p-2 rounded-lg hover:bg-ios-gray-6">
                <XCircle size={18} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Info */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-ios-gray-6 rounded-[10px] p-3">
                  <p className="text-[10px] text-ios-gray uppercase tracking-wider">Cliente</p>
                  <p className="text-sm font-medium mt-0.5">{selectedSale.customer?.name || 'Mostrador'}</p>
                </div>
                <div className="bg-ios-gray-6 rounded-[10px] p-3">
                  <p className="text-[10px] text-ios-gray uppercase tracking-wider">Método de pago</p>
                  <p className="text-sm font-medium mt-0.5">{paymentLabels[selectedSale.paymentMethod] || selectedSale.paymentMethod}</p>
                </div>
                <div className="bg-ios-gray-6 rounded-[10px] p-3">
                  <p className="text-[10px] text-ios-gray uppercase tracking-wider">Vendedor</p>
                  <p className="text-sm font-medium mt-0.5">{selectedSale.user?.fullName}</p>
                </div>
                <div className="bg-ios-gray-6 rounded-[10px] p-3">
                  <p className="text-[10px] text-ios-gray uppercase tracking-wider">Estado</p>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium mt-0.5 ${statusColors[selectedSale.status] || ''}`}>
                    {statusLabels[selectedSale.status] || selectedSale.status}
                  </span>
                </div>
              </div>

              {/* Items */}
              <div>
                <h4 className="text-xs font-medium text-ios-gray mb-2">Productos</h4>
                <div className="space-y-1.5">
                  {selectedSale.items?.map((item: any) => (
                    <div key={item.id} className="flex items-center justify-between text-sm bg-ios-gray-6 rounded-[8px] p-2.5">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium truncate">{item.product?.name}</p>
                        <p className="text-[10px] text-ios-gray">{item.quantity} x ${Number(item.unitPrice).toFixed(2)}</p>
                      </div>
                      <p className="text-xs font-semibold">${Number(item.total).toFixed(2)}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Totals */}
              <div className="border-t border-ios-gray-5 pt-3 space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-ios-gray">Subtotal</span>
                  <span>${Number(selectedSale.subtotal).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-ios-gray">Descuento</span>
                  <span className="text-ios-green">-${Number(selectedSale.discountTotal).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-ios-gray">IVA</span>
                  <span>${Number(selectedSale.taxTotal).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-base font-bold pt-2 border-t border-ios-gray-5">
                  <span>Total</span>
                  <span className="text-ios-blue">${Number(selectedSale.grandTotal).toFixed(2)}</span>
                </div>
              </div>

              {selectedSale.status === 'COMPLETED' && (
                <button
                  onClick={() => {
                    setShowDetail(false);
                    setShowReturnModal(true);
                  }}
                  className="ios-button w-full mt-2 flex items-center justify-center gap-2 !bg-orange-50 !text-orange-600 hover:!bg-orange-100 !border-orange-200"
                >
                  <RotateCcw size={16} />
                  Generar Devolución
                </button>
              )}

              {selectedSale.status === 'CANCELLED' && selectedSale.cancelReason && (
                <div className="bg-red-50 rounded-[10px] p-3">
                  <p className="text-xs text-ios-red font-medium">Cancelada</p>
                  <p className="text-xs text-ios-red/70 mt-0.5">{selectedSale.cancelReason}</p>
                </div>
              )}

              {selectedSale.status === 'PENDING_PAYMENT' && !showPayment && (
                <button
                  onClick={() => setShowPayment(true)}
                  className="ios-button w-full mt-2 flex items-center justify-center gap-2"
                >
                  <CreditCard size={16} />
                  Cobrar Nota
                </button>
              )}

              {showPayment && selectedSale.status === 'PENDING_PAYMENT' && (
                <div className="mt-4 border-t border-ios-gray-5 pt-4">
                  <h4 className="font-semibold mb-3">Registrar Pago</h4>
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    {[
                      { id: 'CASH' as const, label: 'Efectivo', icon: Banknote },
                      { id: 'CARD' as const, label: 'Tarjeta', icon: CreditCard },
                      { id: 'TRANSFER' as const, label: 'Transferencia', icon: ArrowRight },
                      { id: 'CREDIT' as const, label: 'Crédito', icon: User },
                    ].map(({ id, label, icon: Icon }) => (
                      <button
                        key={id}
                        onClick={() => setPaymentMethod(id)}
                        className={`flex items-center gap-2 px-3 py-2 rounded-[10px] text-sm font-medium transition-all ${
                          paymentMethod === id
                            ? 'bg-ios-blue text-white shadow-md'
                            : 'bg-ios-gray-6 text-ios-gray hover:bg-ios-gray-5'
                        }`}
                      >
                        <Icon size={16} />
                        {label}
                      </button>
                    ))}
                  </div>

                  {paymentMethod === 'CASH' && (
                    <div className="mb-3">
                      <label className="text-xs text-ios-gray mb-1 block">Monto recibido</label>
                      <input
                        type="number"
                        value={cashReceived}
                        onChange={(e) => setCashReceived(e.target.value)}
                        placeholder="0.00"
                        className="ios-input w-full text-lg font-semibold"
                        step="0.01"
                        min="0"
                      />
                    </div>
                  )}

                  {paymentMethod === 'CARD' && (
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      <div>
                        <label className="text-xs text-ios-gray mb-1 block">Autorización</label>
                        <input
                          type="text"
                          value={cardReference}
                          onChange={(e) => setCardReference(e.target.value)}
                          className="ios-input w-full text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-ios-gray mb-1 block">Últimos 4</label>
                        <input
                          type="text"
                          value={cardLast4}
                          onChange={(e) => setCardLast4(e.target.value)}
                          maxLength={4}
                          className="ios-input w-full text-sm"
                        />
                      </div>
                    </div>
                  )}

                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowPayment(false)}
                      className="flex-1 px-4 py-2.5 rounded-[10px] text-sm border border-ios-gray-5 hover:bg-ios-gray-6"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={handlePay}
                      disabled={paying || (paymentMethod === 'CASH' && (!cashReceived || parseFloat(cashReceived) < Number(selectedSale.grandTotal)))}
                      className="flex-1 px-4 py-2.5 rounded-[10px] text-sm bg-ios-blue text-white font-medium hover:bg-ios-blue-600 disabled:opacity-50"
                    >
                      {paying ? 'Procesando...' : 'Confirmar Cobro'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      </>
      )}
      {showAdminAuth && (
        <AdminAuthModal
          isOpen={showAdminAuth}
          onClose={() => {
            setShowAdminAuth(false);
            setPendingCancelId(null);
          }}
          onSuccess={handleAdminAuthSuccess}
          actionName="cancelar la venta"
        />
      )}

      {showReturnModal && (
        <ReturnModal
          isOpen={showReturnModal}
          onClose={() => setShowReturnModal(false)}
        />
      )}

      {/* Hidden ticket for printing */}
      <div className="hidden">
        {saleToPrint && <SaleTicket sale={saleToPrint} ref={printRef} />}
      </div>
        </div>
      </div>
    </div>
  );
}
